import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Loader2, XCircle, ExternalLink, RotateCw } from 'lucide-react';
import { inviteApi, AUDIENCE_TYPES } from '../../lib/inviteApi';
import { useSearchProgressShared } from '../../lib/SearchProgressContext';
import InviteStatusBadge from '../../components/invite/InviteStatusBadge';

const LOADING_LINES = [
  'Reading exhibition context…',
  'Building exclusion map…',
  'Searching public cultural sources…',
  'Collecting possible guests…',
  'Preparing results for review…',
];

export default function ReachOutTab({ exhibitionId, onNavigate }) {
  const [audience, setAudience] = useState('curator');
  const [location, setLocation] = useState('');
  const [angle, setAngle] = useState('');
  const [limit, setLimit] = useState(20);
  const [adminNote, setAdminNote] = useState('');
  const [startingRequest, setStartingRequest] = useState(false);
  const progress = useSearchProgressShared();
  const search = progress.snapshot;
  // Tracks the async qualify toast so we can swap it once the poller sees the
  // qualification_status transition to a terminal state.
  const qualifyToastRef = useRef(null);

  useEffect(() => {
    const tracker = qualifyToastRef.current;
    if (!tracker || !search || search.id !== tracker.searchId) return;
    const status = search.qualification_status;
    if (status === tracker.lastStatus) return;
    tracker.lastStatus = status;
    if (status === 'completed') {
      const summary = search.qualification_summary || {};
      const parts = [];
      if (summary.vip) parts.push(`${summary.vip} VIP`);
      if (summary.high) parts.push(`${summary.high} high`);
      if (summary.duplicates) parts.push(`${summary.duplicates} dup`);
      if (summary.suppressed) parts.push(`${summary.suppressed} suppressed`);
      if (summary.average_score != null) parts.push(`avg ${summary.average_score}`);
      const updated = summary.updated || 0;
      toast.success(
        `Qualification complete · ${updated} updated${parts.length ? ' · ' + parts.join(' · ') : ''}`,
        { id: tracker.toastId },
      );
      if (summary.ai_ok === false && summary.ai_error) {
        toast.message('Deterministic dedupe only', { description: summary.ai_error });
      }
      qualifyToastRef.current = null;
    } else if (status === 'failed') {
      toast.error(
        `Qualification failed${search.qualification_error_message ? ': ' + search.qualification_error_message : ''}`,
        { id: tracker.toastId },
      );
      qualifyToastRef.current = null;
    }
  }, [search]);

  // Dismiss any in-flight qualify loading toast on unmount.
  useEffect(() => () => {
    const tracker = qualifyToastRef.current;
    if (tracker) {
      try { toast.dismiss(tracker.toastId); } catch { /* ignore */ }
      qualifyToastRef.current = null;
    }
  }, []);

  const runSearch = async () => {
    if (startingRequest || search?.status === 'running') return;
    setStartingRequest(true);
    try {
      const res = await inviteApi.createSearch({
        exhibition_id: exhibitionId,
        audience_type: audience,
        location_query: location,
        search_angle: angle,
        requested_limit: parseInt(limit, 10) || 20,
        admin_note: adminNote,
      });
      toast.success('Search started · running in the background');
      progress.start(res.search_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start search');
    } finally {
      setStartingRequest(false);
    }
  };

  const cancel = async () => {
    try { await progress.cancel(); toast.message('Search cancelled'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Cancel failed'); }
  };
  const retry = () => runSearch();
  const goToResults = () => onNavigate?.('search-results');

  const qualify = async () => {
    if (!progress.activeSearchId || search?.status !== 'completed') return;
    if (search?.qualification_status === 'qualifying') {
      toast.message('Qualification already running.');
      return;
    }
    const toastId = toast.loading('Qualifying prospects… AI processing can take a moment.');
    qualifyToastRef.current = { searchId: progress.activeSearchId, toastId, lastStatus: 'qualifying' };
    try {
      await inviteApi.qualifySearch(progress.activeSearchId);
      // Trigger an immediate refresh so the badge appears without waiting
      // for the next poll tick.
      await progress.refresh();
    } catch (err) {
      qualifyToastRef.current = null;
      toast.error(err.response?.data?.detail || 'Qualify failed', { id: toastId });
    }
  };

  const isRunning = search?.status === 'running';
  const isCompleted = search?.status === 'completed';
  const isFailed = search?.status === 'failed';
  const isCancelled = search?.status === 'cancelled';
  const isStuck = search?.is_stuck;
  const pct = Math.min(100, Math.max(0, search?.progress_percent ?? 0));
  const stageIndex = Math.min(
    LOADING_LINES.length - 1,
    Math.floor(((search?.elapsed_seconds ?? 0) / Math.max(1, search?.estimated_seconds ?? 1)) * LOADING_LINES.length),
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="reach-out-tab">
      <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-5 space-y-4">
        <div className="flex items-center gap-2.5">
          <span className="block w-1.5 h-1.5 rounded-full" style={{ background: '#90E0EF', boxShadow: '0 0 8px rgba(144,224,239,0.7)' }} />
          <h3 className="font-outfit text-base font-semibold text-white tracking-tight">New AI Search</h3>
        </div>
        <p className="text-[11px] font-mono text-[#777] leading-relaxed">
          The Search Agent reads the exhibition context, builds an exclusion map from existing contacts, tickets, and artists,
          then proposes publicly-discoverable prospects.
        </p>

        <Field label="Audience type">
          <select value={audience} onChange={(e) => setAudience(e.target.value)} className="invite-select w-full" data-testid="reach-out-audience">
            {AUDIENCE_TYPES.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
          </select>
        </Field>
        <Field label="Location / region">
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Berlin, EU, Eastern Europe" className="invite-input w-full" data-testid="reach-out-location" />
        </Field>
        <Field label="Search angle">
          <textarea value={angle} onChange={(e) => setAngle(e.target.value)} rows={3} placeholder="e.g. curators of post-digital figuration who travel for intimate venues" className="invite-input w-full" data-testid="reach-out-angle" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Requested limit">
            <select value={limit} onChange={(e) => setLimit(e.target.value)} className="invite-select w-full" data-testid="reach-out-limit">
              {[10, 20, 30, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <Field label="Admin note">
            <input value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="optional context" className="invite-input w-full" data-testid="reach-out-note" />
          </Field>
        </div>

        <button
          onClick={runSearch}
          disabled={startingRequest || isRunning}
          data-clickable="true"
          data-testid="reach-out-search-btn"
          className={`inline-flex items-center justify-center gap-2 text-[11px] font-mono px-4 py-2.5 border uppercase tracking-[0.14em] transition-all ${
            startingRequest || isRunning
              ? 'border-[#333] text-[#666] cursor-not-allowed'
              : 'border-[#90E0EF] text-[#90E0EF] bg-[#90E0EF]/5 hover:bg-[#90E0EF]/10'
          }`}
        >
          {startingRequest ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" />Starting…</>)
            : isRunning ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" />Running in background…</>)
            : (<><Search className="w-3.5 h-3.5" />Run Search</>)}
        </button>
        {isRunning && (
          <p className="text-[10px] font-mono text-[#666] leading-relaxed">
            AI search can take a few minutes. You can leave this tab and return later — the search will keep running.
          </p>
        )}
      </div>

      <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-5 space-y-4 min-h-[320px]" data-testid="reach-out-status">
        <AnimatePresence mode="wait">
          {!search && !startingRequest && (
            <motion.p key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[11px] font-mono text-[#666] leading-relaxed pt-2">
              The search progress will appear here. Drafts are never auto-sent — every prospect goes through
              Qualify and Convert before becoming an invite.
            </motion.p>
          )}

          {startingRequest && !search && (
            <motion.div key="starting" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 pt-2">
              <p className="text-[10px] font-mono tracking-[0.2em] text-[#90E0EF] uppercase">Starting</p>
              <div className="h-1 bg-[#101010] overflow-hidden">
                <motion.div initial={{ width: '0%' }} animate={{ width: '20%' }} transition={{ duration: 0.6 }} className="h-full bg-[#90E0EF]" />
              </div>
            </motion.div>
          )}

          {search && (isRunning || isCompleted || isFailed || isCancelled) && (
            <motion.div key="progress" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-[10px] font-mono tracking-[0.2em] text-[#90E0EF] uppercase">Search · {search.audience_type}</p>
                  <p className="text-sm font-outfit text-white mt-0.5">{search.location_query || 'any region'} · limit {search.requested_limit}</p>
                </div>
                <InviteStatusBadge status={isStuck ? 'paused' : search.status} strong />
              </div>

              <div>
                <div className="flex items-center justify-between text-[10px] font-mono text-[#777] mb-1.5">
                  <span>{search.elapsed_seconds}s elapsed{search.estimated_seconds ? ` · ~${search.estimated_seconds}s est.` : ''}</span>
                  <span>{pct}%</span>
                </div>
                <div className="h-1 bg-[#101010] overflow-hidden">
                  <motion.div
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className={`h-full ${isFailed || isCancelled ? 'bg-[#e88ba0]' : 'bg-[#90E0EF]'}`}
                    style={isFailed || isCancelled ? {} : { boxShadow: '0 0 12px rgba(144,224,239,0.4)' }}
                  />
                </div>
              </div>

              {isRunning && (
                <>
                  <div className="space-y-2 pt-2">
                    {LOADING_LINES.map((line, i) => (
                      <motion.div key={line} animate={{ opacity: i <= stageIndex ? 1 : 0.25 }} className="flex items-center gap-3 text-xs font-mono">
                        <span className={`block w-1.5 h-1.5 rounded-full ${i <= stageIndex ? 'bg-[#90E0EF]' : 'bg-[#333]'}`} style={i <= stageIndex ? { boxShadow: '0 0 8px rgba(144,224,239,0.7)' } : {}} />
                        <span className={i <= stageIndex ? 'text-[#ccc]' : 'text-[#444]'}>{line}</span>
                      </motion.div>
                    ))}
                  </div>
                  <p className="text-[11px] font-mono text-[#888] pt-2 border-t border-[#161616]">
                    Search is running. Existing invite data stays available — feel free to keep working.
                  </p>
                </>
              )}
              {isStuck && (
                <p className="text-[11px] font-mono text-[#e4c07a]">
                  This search has been running over 20 minutes — it may be stuck. You can cancel and retry.
                </p>
              )}
              {isCompleted && (
                <div className="space-y-2 pt-2">
                  <p className="text-sm font-outfit text-white">{search.result_count} prospects saved.</p>
                  <p className="text-[11px] font-mono text-[#888]">Search completed. Results are ready for review.</p>
                </div>
              )}
              {isFailed && (
                <div className="space-y-2 pt-2">
                  <p className="text-sm font-outfit text-[#e88ba0]">Search failed.</p>
                  <p className="text-[11px] font-mono text-[#888]">Existing invite data is still available.</p>
                  {search.error_message && (
                    <p className="text-[11px] font-mono text-[#aaa] leading-relaxed break-words">{search.error_message}</p>
                  )}
                </div>
              )}
              {isCancelled && (<p className="text-sm font-outfit text-[#aaa] pt-2">Cancelled.</p>)}

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#161616]">
                {isRunning && (
                  <button onClick={cancel} data-clickable="true" data-testid="reach-out-cancel" className="inline-flex items-center gap-1.5 text-[10px] font-mono px-3 py-1.5 border border-[#333] text-[#aaa] hover:text-white uppercase tracking-[0.14em]">
                    <XCircle className="w-3 h-3"/> Cancel
                  </button>
                )}
                {(isFailed || isCancelled) && (
                  <button onClick={retry} data-clickable="true" data-testid="reach-out-retry" className="inline-flex items-center gap-1.5 text-[10px] font-mono px-3 py-1.5 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 uppercase tracking-[0.14em]">
                    <RotateCw className="w-3 h-3"/> Retry search
                  </button>
                )}
                {isCompleted && (() => {
                  const q = search.qualification_status;
                  const isQualifying = q === 'qualifying';
                  const isFailedQ = q === 'failed';
                  return (
                    <button
                      onClick={qualify}
                      disabled={isQualifying}
                      data-clickable="true"
                      data-testid="reach-out-qualify"
                      className={`inline-flex items-center gap-1.5 text-[10px] font-mono px-3 py-1.5 border uppercase tracking-[0.14em] ${
                        isQualifying
                          ? 'border-[#333] text-[#666] cursor-not-allowed'
                          : isFailedQ
                            ? 'border-[rgba(232,139,160,0.5)] text-[#e88ba0] hover:bg-[rgba(232,139,160,0.06)]'
                            : 'border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10'
                      }`}
                    >
                      {isQualifying && <Loader2 className="w-3 h-3 animate-spin"/>}
                      {isFailedQ && !isQualifying && <RotateCw className="w-3 h-3"/>}
                      {isQualifying
                        ? `Qualifying… ${search.qualification_progress_percent || 0}%`
                        : (isFailedQ ? 'Retry Qualify' : 'Qualify / Dedupe')}
                    </button>
                  );
                })()}
                {isCompleted && search.qualification_status === 'failed' && search.qualification_error_message && (
                  <span className="text-[10px] font-mono text-[#e88ba0] inline-flex items-center gap-1">
                    {search.qualification_error_message}
                  </span>
                )}
                <button onClick={goToResults} data-clickable="true" data-testid="reach-out-view-results" className="inline-flex items-center gap-1.5 text-[10px] font-mono px-3 py-1.5 border border-[#333] text-[#aaa] hover:text-white uppercase tracking-[0.14em]">
                  <ExternalLink className="w-3 h-3"/> View in Search Results
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">{label}</span>
      {children}
    </label>
  );
}
